import React, { useMemo } from 'react';
import { X, Shield, ArrowUpRight, Navigation } from 'lucide-react';
import { Button } from '../UI/button';
import { Badge } from '../UI/badge';
import { useLocale } from '../../contexts/LocaleContext';
import { colors } from '../../config/colors';
import { cn } from '../../lib/utils';
import { buildHighRiskCompanyMarkers } from '../../utils/watchlistMapModel';
import {
  buildHighRiskSupplierMarkers,
  getMarkerLocationLabel,
  scoreToRiskLevelKey,
} from '../../utils/tenantMapModel';

const riskBadgeClass = (levelKey) => {
  if (levelKey === 'high') return 'bg-danger-500/15 text-danger-500 border-danger-500/30';
  if (levelKey === 'medium') return 'bg-warning-500/15 text-warning-500 border-warning-500/30';
  if (levelKey === 'low') return 'bg-success-500/15 text-success-500 border-success-500/30';
  return 'bg-background-secondary text-text-secondary border-border-light';
};

function formatRiskScore(score) {
  const value = Number(score);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value >= 10 ? value.toFixed(1) : String(Math.round(value * 10) / 10);
}

function priorityLabelKey(priority) {
  const p = String(priority || '').toLowerCase();
  if (p === 'high') return 'riskLevelHigh';
  if (p === 'low') return 'riskLevelLow';
  return 'riskLevelModerate';
}

function WatchlistMapDetailPanel({
  marker,
  route,
  markers = [],
  onClose,
  onMarkerSelect,
  onViewRouteAnalysis,
  className,
}) {
  const { t } = useLocale();

  const highRiskCompanies = useMemo(() => {
    if (!marker || route) return [];
    if (marker.type === 'own') {
      return buildHighRiskSupplierMarkers(markers);
    }
    return buildHighRiskCompanyMarkers(markers);
  }, [marker, route, markers]);

  if (route) {
    const formattedRisk = formatRiskScore(route.riskScore);
    const riskLevelKey = route.riskLevel || scoreToRiskLevelKey(route.riskScore);

    return (
      <aside
        className={cn(
          'absolute bottom-4 right-4 top-4 z-20 flex w-[min(100%,340px)] flex-col overflow-hidden rounded-xl border border-border-light bg-background-card/95 shadow-xl backdrop-blur-md',
          className
        )}
      >
        <div className="flex items-start justify-between gap-3 border-b border-border-light px-4 py-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <Navigation className="h-4 w-4 shrink-0 text-secondary-500" aria-hidden />
              <h3 className="truncate text-base font-semibold text-text-primary">
                {route.title || t('routesTitle')}
              </h3>
            </div>
            {route.subtitle ? (
              <p className="mt-1 text-xs text-text-secondary">{route.subtitle}</p>
            ) : null}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            className="shrink-0"
            onClick={onClose}
            aria-label={t('watchlistMapPanelClose')}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto custom-scrollbar px-4 py-4">
          {formattedRisk ? (
            <Badge
              variant="outline"
              className={cn('mb-4 rounded-md px-2.5 py-1 text-sm font-semibold', riskBadgeClass(riskLevelKey))}
            >
              {t('watchlistMapRiskBadge', {
                score: formattedRisk,
                level: t(`tenantMapRiskLevel_${riskLevelKey}`),
              })}
            </Badge>
          ) : null}

          <Badge variant="outline" className="mb-4">
            {route.isActive === false ? t('routeDetailsInactive') : t('active')}
          </Badge>

          <div className="mt-5">
            <Button
              type="button"
              variant="default"
              size="sm"
              className="w-full justify-between"
              onClick={() => onViewRouteAnalysis?.(route)}
            >
              {t('watchlistMapViewAnalysis')}
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      </aside>
    );
  }

  if (!marker) return null;

  const entityTypeLabel =
    marker.type === 'own'
      ? t('tenantMapEntityTypeOwn')
      : marker.type === 'supplier'
        ? t('tenantMapEntityTypeSupplier')
        : t('watchlistMapDetailCompanyType');

  const formattedRisk = formatRiskScore(marker.riskScore);
  const riskLevelKey = scoreToRiskLevelKey(marker.riskScore);
  const locationLabel = getMarkerLocationLabel(marker);

  return (
    <aside
      className={cn(
        'absolute bottom-4 right-4 top-4 z-20 flex w-[min(100%,340px)] flex-col overflow-hidden rounded-xl border border-border-light bg-background-card/95 shadow-xl backdrop-blur-md',
        className
      )}
    >
      <div className="flex items-start justify-between gap-3 border-b border-border-light px-4 py-4">
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-base font-semibold text-text-primary">{marker.title}</h3>
          <p className="mt-1 text-xs text-text-secondary">
            {t('watchlistMapNetworkCount', { count: markers.length })}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="shrink-0"
          onClick={onClose}
          aria-label={t('watchlistMapPanelClose')}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar px-4 py-4">
        {formattedRisk ? (
          <div className="mb-4 flex items-center gap-2">
            <Badge
              variant="outline"
              className={cn('rounded-md px-2.5 py-1 text-sm font-semibold', riskBadgeClass(riskLevelKey))}
            >
              {t('watchlistMapRiskBadge', {
                score: formattedRisk,
                level: t(`tenantMapRiskLevel_${riskLevelKey}`),
              })}
            </Badge>
          </div>
        ) : null}

        {locationLabel ? (
          <p className="text-sm text-text-primary">{locationLabel}</p>
        ) : null}

        {marker.priority ? (
          <p className="mt-1 text-xs text-text-secondary">
            {t('watchlistMapDetailPriority')}: {t(priorityLabelKey(marker.priority))}
          </p>
        ) : null}

        <p className="mt-1 text-xs text-text-secondary">{entityTypeLabel}</p>

        {marker.companyType ? (
          <p className="mt-1 text-xs text-text-secondary">
            {t('watchlistMapDetailCompanyType')}: {marker.companyType}
          </p>
        ) : null}

        {marker.operationalStatus ? (
          <p className="mt-1 text-xs text-text-secondary">
            {t('tenantMapDetailOperationalStatus')}: {marker.operationalStatus}
          </p>
        ) : null}

        {marker.routeCount != null ? (
          <p className="mt-1 text-xs text-text-secondary">
            {t('watchlistMapDetailRouteCount', { count: marker.routeCount })}
          </p>
        ) : null}

        {marker.companyIds?.length > 1 ? (
          <p className="mt-2 text-xs text-text-tertiary">
            {t('watchlistMapSharedLocation', { count: marker.companyIds.length })}
          </p>
        ) : null}

        <div className="mt-5">
          {marker.companyId || marker.supplierTicker ? (
            <Button type="button" variant="default" size="sm" className="w-full justify-between" asChild>
              <a
                href={`/companies/${encodeURIComponent(marker.companyId || marker.supplierTicker)}`}
              >
                {t('watchlistMapViewProfile')}
                <ArrowUpRight className="h-3.5 w-3.5" />
              </a>
            </Button>
          ) : null}
        </div>

        {marker.type === 'own' && highRiskCompanies.length > 0 ? (
          <div className="mt-6">
            <div className="mb-3 flex items-baseline justify-between gap-2">
              <p className="text-sm font-semibold text-text-primary">
                {t('watchlistMapHighRiskCompanies', { count: highRiskCompanies.length })}
              </p>
              <span className="text-xs text-text-tertiary">{t('watchlistMapSortedHighestRisk')}</span>
            </div>
            <ul className="space-y-2">
              {highRiskCompanies.map((company) => {
                const companyRisk = formatRiskScore(company.riskScore);
                const companyLevel = scoreToRiskLevelKey(company.riskScore);
                return (
                  <li key={company.id}>
                    <button
                      type="button"
                      onClick={() => onMarkerSelect?.(company.id)}
                      className="flex w-full items-center gap-3 rounded-lg border border-border-light bg-background-secondary px-3 py-2.5 text-left transition-colors hover:border-secondary-500/40 hover:bg-canvas-soft-2"
                    >
                      <Shield
                        className="h-4 w-4 shrink-0"
                        style={{ color: colors.secondary[500] }}
                        aria-hidden
                      />
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-text-primary">
                        {company.title}
                      </span>
                      {companyRisk ? (
                        <span className="shrink-0 text-xs font-semibold text-danger-500">
                          {t('watchlistMapRiskBadgeShort', {
                            score: companyRisk,
                            level: t(`tenantMapRiskLevel_${companyLevel}`),
                          })}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </div>
    </aside>
  );
}

export default WatchlistMapDetailPanel;
